// One Action Center row (DC-44, DC-46; V1-INTERACTIONS §4.27): the server's `action_center.list` row drawn as the
// design system's ActionRow — kind icon · verb — object · requisition title + mono id (a request-stage row: the
// request + its mono id; a row on neither: the Candidate ID) · owner · SLA clock · due date · chevron, the whole row one
// link to the exact screen. History rows are the `.hist` form: Completed, by whom, when.
// There is no dismiss control: a row leaves the list only when the act it asks for happens (auto-close, DC-47).
//
// Every word on the row is the server's (his verb and object, the item's server-built title, the requisition title
// the reader may open, the owner slot, the due phrase and its Calendar Time) or the design system's; nothing is
// composed here. While a producer has no verb for its gate the row shows its title, split at its first colon into the
// verb and object places (D17's fallback); a title that ends in " · " and the row's own record id is that verb, its
// object the request's title (none for a requisition: the second line names it). Beside the server's verb and object
// the request's title names the record on the second line. An urgent request's row (the server's `urgent`, DC-234
// F-143) carries *Urgent* as the row's flag pill after the verb.

import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ACTION_LABELS, ActionRow as McActionRow, Banner, Button, Flex, templatePattern, type ActionKind, type DueTier, type Hs } from "@/mc";
import type { ActionRow as ActionItemRow, SchedulingRespondInput } from "@/api/tools";
import { schedulingRespond } from "@/api/tools";
import { newIdempotencyKey } from "@/api/runtime";
import { errorText } from "@/api/useTool";
import { TIMING_LABELS } from "../request/labels";
import { RESPOND_LABELS, RESPOND_TEMPLATES, SCHEDULING_ROW_TEMPLATES } from "./labels";

export const ACTION_KINDS: readonly ActionKind[] = [
  "reqs", "candidates", "scheduling", "packets", "scorecards", "panels", "approvals", "offers", "closeout",
];

/** The five SLA tiers in priority order (DC-45; the server's ACTION_TIERS). */
export const ACTION_TIERS = ["breach", "near_breach", "reminder", "due", "blocked"] as const;
export type ActionTier = (typeof ACTION_TIERS)[number];

export function kindOf(category: string): ActionKind {
  return (ACTION_KINDS as readonly string[]).includes(category) ? (category as ActionKind) : "reqs";
}

/** The server's tier as the clock's tier (DC-19): a breached scorecard is Past Due, every other breach Overdue. */
export function dueTierOf(tier: string, kind: ActionKind): DueTier {
  if (tier === "breach") return kind === "scorecards" ? "pastDue" : "overdue";
  if (tier === "near_breach") return "nearBreach";
  if (tier === "reminder") return "reminder";
  return "due";
}

type RowWords = Pick<ActionItemRow, "verb" | "title" | "requisition_id" | "request_id" | "request_title" | "object_word"> & Partial<Pick<ActionItemRow, "category" | "responses">>;
type RowRecord = Pick<
  ActionItemRow,
  "requisition_id" | "requisition_title" | "request_id" | "request_detail" | "request_title" | "verb" | "candidate_no"
>;

/** The title without a trailing " · {id}", when it ends with one. */
function headBefore(title: string, id: string | null | undefined): string | null {
  const tail = id ? ` · ${id}` : "";
  return tail && title.length > tail.length && title.endsWith(tail) ? title.slice(0, -tail.length) : null;
}

/** The scheduling titles drawn whole (F-159, F-162): "Reschedule request · Intake call · R-1048", "Intake call
 * scheduled", "Reschedule — Marcus declined" — the server's verb never splits them into "Reschedule — request · …". */
const WHOLE_TITLES: readonly RegExp[] = Object.values(SCHEDULING_ROW_TEMPLATES).map((t) => templatePattern(t.text));
export function isWholeTitle(row: { title: string; category?: string | undefined; responses?: ActionItemRow["responses"] | undefined }): boolean {
  return !!row.responses || (row.category === "scheduling" && WHOLE_TITLES.some((p) => p.test(row.title)));
}

/** The verb and object places: his verb and the item's title, or the title split at its first colon, or a title that
 * ends with the row's own record id as the verb (the object the request's title the reader may open). */
export function verbAndObject(row: RowWords): { verb: string; object: string } {
  if (isWholeTitle(row)) return { verb: row.title, object: "" };
  if (row.verb) return { verb: row.verb, object: row.object_word ?? row.title };
  const at = row.title.indexOf(": ");
  if (at > 0) return { verb: row.title.slice(0, at), object: row.title.slice(at + 2) };
  const onRequisition = headBefore(row.title, row.requisition_id);
  if (onRequisition !== null) return { verb: onRequisition, object: "" };
  const onRequest = headBefore(row.title, row.request_id);
  if (onRequest !== null) return { verb: onRequest, object: row.request_title ?? "" };
  return { verb: row.title, object: "" };
}

/** The second line: the requisition's title and id; else a request-stage row's request (its title beside the
 * server's verb, else its department or level) and id; else the Candidate ID of the profile it is about. */
export function recordOf(row: RowRecord): { reqTitle: string; reqId: string } {
  if (row.requisition_id) return { reqTitle: row.requisition_title ?? "", reqId: row.requisition_id };
  if (row.request_id) {
    const named = row.verb ? row.request_title ?? row.request_detail : row.request_detail;
    return { reqTitle: named ?? "", reqId: row.request_id };
  }
  return { reqTitle: "", reqId: row.candidate_no ?? "" };
}

export interface ActionCenterRowProps {
  row: ActionItemRow;
  /** The History tab's form (Completed · by {actor} · {date/time}). */
  history?: boolean | undefined;
  /** The row was resolved since the last read: it collapses in 200 ms, then onClosed runs. */
  closing?: boolean | undefined;
  onClosed?: (() => void) | undefined;
  hs?: Hs | undefined;
}

/** A tracking row (F-159, DC-238): the server's title, then *responses {n} of {m}* (the invited attendees, never the
 *  organizer). Any other row's words are untouched. */
export function trackedWords(row: Pick<ActionItemRow, "responses">, words: { verb: string; object: string }): { verb: string; object: string } {
  if (!row.responses) return words;
  const count = RESPOND_TEMPLATES.responses({ n: row.responses.answered, m: row.responses.of });
  return words.object ? { verb: words.verb, object: `${words.object} · ${count}` } : { verb: `${words.verb} · ${count}`, object: "" };
}

/** The three answers to an invitation, in the order the row draws them (DC-238: Accept · Tentative · Decline). */
export const RESPOND_ANSWERS: readonly { response: SchedulingRespondInput["response"]; label: string; tone: "primary" | "ghost" }[] = [
  { response: "accepted", label: ACTION_LABELS.accept, tone: "primary" },
  { response: "tentative", label: RESPOND_LABELS.tentative, tone: "ghost" },
  { response: "declined", label: ACTION_LABELS.decline, tone: "ghost" },
];

/**
 * F-159 (DC-238): a meeting invitation answered on the row itself — Accept (primary) · Tentative · Decline, the same
 * `scheduling.respond` the scheduling card uses (only the attendee answers their own row). The row is a link, so the
 * answers sit in the list item that follows it; the answer re-reads the Action Center, where the row closes (DC-47).
 */
export function RespondAnswers({ row, describedBy, onAnswered }: { row: ActionItemRow; describedBy?: string | undefined; onAnswered?: (() => void) | undefined }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<SchedulingRespondInput["response"] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const respond = row.respond;
  if (!respond) return null;
  const answer = async (response: SchedulingRespondInput["response"]) => {
    if (busy) return;
    setBusy(response);
    setProblem(null);
    try {
      await schedulingRespond({ meeting_event_id: respond.meeting_event_id, response }, newIdempotencyKey());
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "action-center" || q.queryKey[0] === "scheduling.open" || q.queryKey[0] === "today" });
      onAnswered?.();
    } catch (error) {
      setProblem(errorText(error));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Flex gap={8} wrap align="center">
      {RESPOND_ANSWERS.map((a) => (
        <Button
          key={a.response}
          sm
          tone={a.tone}
          busy={busy === a.response}
          aria-disabled={busy !== null && busy !== a.response ? true : undefined}
          aria-describedby={describedBy}
          aria-pressed={respond.response === a.response ? true : undefined}
          onClick={() => void answer(a.response)}
          
        >
          {a.label}
        </Button>
      ))}
      {problem ? <Banner tone="red" live>{problem}</Banner> : null}
    </Flex>
  );
}

export function ActionCenterRow({ row, history, closing, onClosed, hs }: ActionCenterRowProps) {
  const kind = kindOf(row.category);
  const { verb, object } = trackedWords(row, verbAndObject(row));
  const { reqTitle, reqId } = recordOf(row);
  const id = useId();
  const answers = !history && !closing && row.respond ? (
    <li className="mc-arow-w">
      <span id={id} hidden>{verb}{object ? ` — ${object}` : ""}</span>
      <RespondAnswers row={row} describedBy={id} />
    </li>
  ) : null;
  const drawn = (
    <McActionRow
      kind={kind}
      verb={verb}
      object={object}
      reqTitle={reqTitle}
      reqId={reqId}
      href={row.route ?? "#action-center"}
      tier={dueTierOf(row.tier, kind)}
      due={history ? undefined : row.due_at ?? undefined}
      phrase={history ? undefined : row.due_phrase ?? undefined}
      calendar={history ? undefined : row.due_calendar ?? undefined}
      owner={row.owner_word ?? undefined}
      flag={row.urgent ? TIMING_LABELS.urgent : undefined}
      history={history ? { actor: row.closed_by_name ?? "—", at: row.closed_at ?? row.opened_at } : undefined}
      closing={closing}
      onClosed={onClosed}
      hs={hs}
    />
  );
  if (!answers) return drawn;
  return <>{drawn}{answers}</>;
}
